import type { IncidentDetailResponse } from '@prerender/diagnostics-api';
import { useId } from 'react';

import { formatCountOfTotalOrAbsent } from './crawlerEvidenceFigures';
import EvidenceFigure from './EvidenceFigure';
import EvidenceNotes from './EvidenceNotes';
import { EVIDENCE_TITLE } from './EvidenceTable';
import { formatRate } from './formatMeasuredValue';
import {
  blockingFiguresOf,
  formatMeasurementPeriod,
  isRecovered,
  measurementPeriodOf,
  type RateRules,
} from './originBlockingFigures';
import SeverityBands from './SeverityBands';
import UrlImpactFigure from './UrlImpactFigure';

type OriginBlockingEvidenceProps = {
  incident: IncidentDetailResponse;
  rules: RateRules | null;
};

function PeriodLine({ end, windowDays }: { end: string; windowDays: number }) {
  const period = measurementPeriodOf(end, windowDays);

  return (
    <p className="diagnostics-evidence-period">
      {`Last ${windowDays} days · ${formatMeasurementPeriod(period)}`}
    </p>
  );
}

const BLOCKED_REQUESTS_HELP = 'Crawler requests answered 403, 503 or 504, of all crawler requests observed';

const blockedRequestsHelpOf = ({ domains }: IncidentDetailResponse): string =>
  domains.some(isRecovered)
    ? `${BLOCKED_REQUESTS_HELP}, including recovering domains at their last measured figures`
    : BLOCKED_REQUESTS_HELP;

const rateHelpOf = ({ domains, headlineDomain }: IncidentDetailResponse): string =>
  domains.length > 1 ? `Worst domain: ${headlineDomain} — sets the severity` : 'The figure the severity comes from';

const isReadable = (evidence: Record<string, unknown>): boolean => blockingFiguresOf(evidence) !== null;

function OriginBlockingEvidence({ incident, rules }: OriginBlockingEvidenceProps) {
  const titleId = useId();

  return (
    <section className="diagnostics-detail-section" aria-labelledby={titleId}>
      <h2 id={titleId} className="diagnostics-detail-section-title">
        {EVIDENCE_TITLE}
      </h2>
      {rules !== null && <PeriodLine end={incident.lastDetectedAt} windowDays={rules.windowDays} />}
      <dl className="diagnostics-evidence-figures">
        <EvidenceFigure name="Blocked requests" help={blockedRequestsHelpOf(incident)}>
          {formatCountOfTotalOrAbsent(incident.requestImpactCount, incident.requestTotalCount)}
        </EvidenceFigure>
        <EvidenceFigure name="Blocked request rate" help={rateHelpOf(incident)}>
          {formatRate(incident.metricValue)}
        </EvidenceFigure>
        <UrlImpactFigure
          name="URLs that got a block"
          affectedUrlCount={incident.affectedUrlCount}
          crawledUrlCount={incident.crawledUrlCount}
        />
      </dl>
      <EvidenceNotes domains={incident.domains} isReadable={isReadable} />
      {rules !== null && (
        <SeverityBands rules={rules} severity={incident.severity} rate={formatRate(incident.metricValue)} />
      )}
    </section>
  );
}

export default OriginBlockingEvidence;
